import { apiClient } from '@/shared/lib/axios';

/** Params for admin latlon cache list. */
export interface LatlonCacheListParams {
  /** Filter by tenant site_type (array). Empty = all. */
  site_type?: string[];
  clinic_number?: string;
  address?: string;
  sort_by?: string;
  order?: 'asc' | 'desc';
  page?: number;
  per_page?: number;
}

/** One row in admin latlon cache list. */
export interface LatlonCacheListItem {
  id: number;
  clinic_id: number;
  clinic_number: string | null;
  site_type: string | null;
  address: string | null;
  address_geop: string | null;
  latitude: string | null;
  longitude: string | null;
  created_at: string | null;
  updated_at: string | null;
  created_by?: string | null;
  updated_by?: string | null;
}

export interface LatlonCacheListMeta {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
  from: number | null;
  to: number | null;
}

export interface LatlonCacheListResponse {
  success: boolean;
  message?: string;
  data: {
    data: LatlonCacheListItem[];
    meta: LatlonCacheListMeta;
  };
}

export const getLatlonCaches = async (
  site: string,
  params: LatlonCacheListParams
): Promise<LatlonCacheListResponse> => {
  const response = await apiClient.get<LatlonCacheListResponse>(`/admin/${site}/latlon-caches`, {
    params,
  });
  return response.data;
};

/**
 * Download latlon caches as CSV.
 */
export const downloadLatlonCachesCsv = async (
  site: string,
  params: LatlonCacheListParams
): Promise<void> => {
  const { page: _page, per_page: _perPage, ...csvParams } = params;
  const response = await apiClient.get(`/admin/${site}/latlon-caches/csv`, {
    params: csvParams,
    responseType: 'blob',
  });

  const blob = new Blob([response.data], { type: 'text/csv;charset=UTF-8;' });
  const url = window.URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = url;
  a.download = `latlon_cache${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

/** Detail response shape. */
export interface LatlonCacheDetailResponse {
  success: boolean;
  message?: string;
  data: LatlonCacheListItem;
}

/** Update payload. */
export interface LatlonCacheUpdatePayload {
  address: string;
  address_geop?: string | null;
  latitude: string;
  longitude: string;
}

/** Store payload. */
export interface LatlonCacheStorePayload {
  clinic_id: number;
  address: string;
  address_geop?: string | null;
  latitude: string;
  longitude: string;
}

export const getLatlonCache = async (
  site: string,
  id: number
): Promise<LatlonCacheDetailResponse> => {
  const response = await apiClient.get<LatlonCacheDetailResponse>(
    `/admin/${site}/latlon-caches/${id}`
  );
  return response.data;
};

export const updateLatlonCache = async (
  site: string,
  id: number,
  data: LatlonCacheUpdatePayload
): Promise<LatlonCacheDetailResponse> => {
  const response = await apiClient.put<LatlonCacheDetailResponse>(
    `/admin/${site}/latlon-caches/${id}`,
    data
  );
  return response.data;
};

export const createLatlonCache = async (
  site: string,
  data: LatlonCacheStorePayload
): Promise<LatlonCacheDetailResponse> => {
  const response = await apiClient.post<LatlonCacheDetailResponse>(
    `/admin/${site}/latlon-caches`,
    data
  );
  return response.data;
};

export const deleteLatlonCache = async (
  site: string,
  id: number
): Promise<{ success: boolean; message?: string }> => {
  const response = await apiClient.delete<{ success: boolean; message?: string }>(
    `/admin/${site}/latlon-caches/${id}`
  );
  return response.data;
};
